import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import {
    RequestUserModel,
    UserModel,
    UserModelRequest,
} from '../Models/user.model';
import { environment } from '../environments/environment';
import { CompanyModel } from '../Models/delivery';

@Injectable({
    providedIn: 'root',
})
export class LoginService {
    private apiUrl = environment.apiUrl;
    private authUrl = `${environment.authUrl}`;
    private currentUserSubject = new BehaviorSubject<UserModel | null>(null);
    public currentUser$ = this.currentUserSubject.asObservable();

    constructor(
        private http: HttpClient,
        private router: Router,
    ) {
        const user = this.getUser();
        if (user) {
            this.currentUserSubject.next(user);
        }
    }

    /**
     * Logs in a user by sending a POST request to the server with email and password.
     * @param email - The email of the user trying to log in.
     * @param password - The password of the user trying to log in.
     * @returns Observable<{ user: string }> - An observable that emits the user information after a successful login.
     */
    public login(
        email: string,
        password: string,
    ): Observable<{ user: string; token: string }> {
        return this.http
            .post<{ user: string; token: string }>(
                `${this.authUrl}/login`,
                { email, password },
                {
                    withCredentials: true,
                },
            )
            .pipe(
                tap((response) => {
                    sessionStorage.setItem(
                        'user',
                        JSON.stringify(response.user),
                    );
                    sessionStorage.setItem('token', response.token);
                }),
            );
    }

    public refreshToken(): Observable<{ token: string }> {
        return this.http
            .post<{
                token: string;
            }>(`${this.authUrl}/refresh`, {}, { withCredentials: true })
            .pipe(
                tap((response) => {
                    sessionStorage.setItem('token', response.token);
                }),
            );
    }

    /**
     * Function obtains the user account request object and parses it into the
     * Cloudflare worker to be added to the database
     * @param {RequestUserModel} reqUser The requested account data object
     * @returns {Observable<HttpResponse<any>>} An observable that emits an http response from the cloudflare worker
     */
    public requestAccess(
        reqUser: RequestUserModel,
    ): Observable<HttpResponse<any>> {
        return this.http.post<any>(
            `${this.apiUrl}/users/userRequest`,
            reqUser,
            {
                withCredentials: true,
            },
        );
    }

    /**
     * Logs out the current user by sending a POST request to the server and clearing the session storage.
     */
    public logout(): void {
        this.http
            .post(`${this.authUrl}/logout`, {}, { withCredentials: true })
            .subscribe(() => {
                sessionStorage.removeItem('user');
                sessionStorage.removeItem('token');
                this.router.navigate(['/login']);
            });
    }

    /**
     * Checks if a user is currently logged in by checking the session storage.
     * @returns boolean - Returns true if a user is logged in, false otherwise.
     */
    public isLoggedIn(): boolean {
        return !!sessionStorage.getItem('user');
    }

    /**
     * Sets the current user in the session storage and updates the BehaviorSubject.
     * @param user - The user object to be set as the current user.
     */
    public setUser(user: UserModel): void {
        sessionStorage.setItem('user', JSON.stringify(user));
        this.currentUserSubject.next(user);
    }

    /**
     * Retrieves the current user from the session storage.
     * @returns UserModel | null - Returns the current user if available, otherwise null.
     */
    public getUser(): UserModel | null {
        const raw = sessionStorage.getItem('user');
        if (!raw) {
            return null;
        }

        try {
            const parsed = JSON.parse(raw);
            const user: UserModel = {
                id: parsed.id,
                email: parsed.email,
                name: parsed.name,
                role: parsed.role,
                company: {
                    id: parsed.company.id,
                    name: parsed.company.name,
                    code: parsed.company.code,
                    leadTime: parsed.company.leadTime
                },
            };
            return user;
        } catch (err) {
            console.error('Error parsing user from session', err);
            return null;
        }
    }

    /**
     * Retrieves a list of all users from the server.
     * @returns Observable<UserModel[]> - An observable that emits an array of user models.
     */
    public getAllUsers(): Observable<UserModel[]> {
        return this.http
            .get<{ users: UserModel[] }>(`${this.apiUrl}/users/getUsers`, {
                withCredentials: true,
            })
            .pipe(map((response) => response.users));
    }

    /**
     * Obtains an array of all requests for accounts
     * @returns {Observable<RequestUserModel[]>} The array of requested users
     */
    public getAllRequestedUsers(): Observable<RequestUserModel[]> {
        return this.http
            .get<{ requestedUsers: RequestUserModel[] }>(
                `${this.apiUrl}/users/getRequestedUsers`,
                {
                    withCredentials: true,
                },
            )
            .pipe(map((response) => response.requestedUsers));
    }

    /**
     * Retrieves a user by their ID from the server.
     * @param user - The user object containing the user ID.
     * @returns Observable<HttpResponse<any>> - An observable that emits the HTTP response containing the user data.
     */
    public addNewUser(
        user: Partial<UserModelRequest>,
    ): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.apiUrl}/users/addUser`, user, {
            observe: 'response',
            withCredentials: true,
        });
    }

    /**
     * Updates an existing user in the database.
     * @param user - The user object containing the updated user information.
     * @returns Observable<HttpResponse<any>> - An observable that emits the HTTP response after updating the user.
     */
    public editUser(user: UserModelRequest): Observable<HttpResponse<any>> {
        return this.http.patch<any>(`${this.apiUrl}/users/updateUser`, user, {
            observe: 'response',
            withCredentials: true,
        });
    }

    /**
     * Deletes a user from the database.
     * @param user - The user object to be deleted.
     * @returns Observable<HttpResponse<any>> - An observable that emits the HTTP response after deleting the user.
     */
    public deleteUser(userId: number): Observable<HttpResponse<any>> {
        return this.http.delete<any>(`${this.apiUrl}/users/removeUser`, {
            body: {
                id: userId,
            },
            observe: 'response',
            withCredentials: true,
        });
    }

    /**
     * Fetches a list of companies from the backend API.
     * @returns
     */
    public getCompanies(): Observable<CompanyModel[]> {
        return this.http
            .get<{ companies: CompanyModel[] }>(
                `${this.apiUrl}/users/getCompanies`,
                {
                    withCredentials: true,
                },
            )
            .pipe(map((response) => response.companies));
    }

    /**
     * Fetches the current logged-in user's information from the backend API.
     * @returns
     */
    public getCurrentUser(): Observable<UserModel> {
        return this.http
            .get<UserModel>(`${this.apiUrl}/auth/getCurrentUser`, {
                withCredentials: true,
            })
            .pipe(map((response) => response));
    }

    public forgotPassword(email: string): Observable<any> {
        return this.http.post<any>(
            `${this.authUrl}/forgot-password`,
            { email },
            { withCredentials: true },
        );
    }

    public resetPassword(token: string, password: string): Observable<any> {
        return this.http.post<any>(
            `${this.authUrl}/reset-password`,
            { token, password },
            { withCredentials: true },
        );
    }
}
